import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { Order } from '@models/order';

export interface SuperbillMatchKey {
  key: string;
  label: string;
}

export interface SuperbillCode {
  _id?: string;
  displayName: string;
  insuranceCode: string;
  matchKey: string;
  active?: boolean;
  sortOrder?: number;
}

export interface SuperbillProfile {
  _id?: string;
  providerName?: string;
  npi?: string;
  licenseNumber?: string;
  taxId?: string;
  statement?: string;
  signatureNote?: string;
}

export interface SuperbillStore {
  _id: string | number;
  storeName?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  fax?: string;
  email?: string;
  website?: string;
  taxId?: string;
  dispensaryName?: string;
}

export interface SuperbillTemplate {
  name: string;
  updatedAt?: string | null;
  custom?: boolean;
  storeId?: string | number;
  storeName?: string;
}

export interface SuperbillSetup {
  codes: SuperbillCode[];
  profile: SuperbillProfile;
  stores: SuperbillStore[];
  template: SuperbillTemplate;
  matchKeys: SuperbillMatchKey[];
}

@Injectable({
  providedIn: 'root',
})
export class SuperbillService {
  private baseUrl = `${environment.base_url}/superbill`;

  constructor(private http: HttpClient) {}

  getSetup(storeId: string | number): Observable<SuperbillSetup> {
    return this.http.get(`${this.baseUrl}/setup`, { params: { store: String(storeId) } }).pipe(
      map((res: any) => ({
        codes: res?.codes || [],
        profile: res?.profile || {},
        stores: res?.stores || [],
        template: res?.template || { name: 'SUPERBILL-B-Temp.pdf' },
        matchKeys: res?.matchKeys || [],
      }))
    );
  }

  saveCode(code: SuperbillCode): Observable<SuperbillCode> {
    if (code._id) {
      return this.http
        .put(`${this.baseUrl}/codes/${code._id}`, code)
        .pipe(map((res: any) => res?.code));
    }
    return this.http
      .post(`${this.baseUrl}/codes`, code)
      .pipe(map((res: any) => res?.code));
  }

  removeCode(id: string): Observable<void> {
    return this.http
      .delete(`${this.baseUrl}/codes/${id}`)
      .pipe(map(() => undefined));
  }

  saveStore(store: SuperbillStore): Observable<SuperbillStore> {
    return this.http
      .put(`${this.baseUrl}/store/${store._id}`, store)
      .pipe(map((res: any) => res?.store));
  }

  templatePdf(storeId: string | number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/template`, {
      params: { store: String(storeId) },
      responseType: 'blob',
    });
  }

  uploadTemplate(file: File, storeId: string | number): Observable<SuperbillTemplate> {
    const body = new FormData();
    body.append('store', String(storeId));
    body.append('template', file, file.name);
    return this.http
      .post(`${this.baseUrl}/template`, body)
      .pipe(map((res: any) => res?.template));
  }

  saveProfile(profile: SuperbillProfile): Observable<SuperbillProfile> {
    return this.http
      .put(`${this.baseUrl}/profile`, profile)
      .pipe(map((res: any) => res?.profile));
  }

  pdf(orderId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/order/${orderId}/pdf`, {
      responseType: 'blob',
    });
  }

  email(orderId: string, email?: string): Observable<Order> {
    return this.http
      .post(`${this.baseUrl}/order/${orderId}/email`, { email: email || '' })
      .pipe(map((res: any) => res?.order));
  }

  retry(orderId: string): Observable<Order> {
    return this.http
      .post(`${this.baseUrl}/order/${orderId}/retry`, {})
      .pipe(map((res: any) => res?.order));
  }

  openPdf(blob: Blob, print: boolean): void {
    const file = new Blob([blob], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(file);
    if (!print) {
      window.open(url, '_blank');
      return;
    }
    const html = `
      <embed src="${url}" style="position:absolute;left:0;top:0;" width="100%" height="100%" type="application/pdf">
      <script type="text/javascript">
      setTimeout(function() { window.print(); }, 500);
      </script>
    `;
    const preview = window.open(
      '',
      '',
      'left=0,top=0,width=900,height=900,toolbar=0,scrollbars=0,status=0'
    );
    if (preview) {
      preview.document.write(html);
    }
  }
}
